"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _base = require("@site/pattern-library-utils/base.functions");
var _scrollContext = require("./scroll-context");
var _reactIntersectionObserver = require("react-intersection-observer");
var _scrollSpring = require("./scroll-spring");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable max-statements */

const Container = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "Container",
  class: "pl-c16xb01l"
});
function throttle(func, wait) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall >= wait) {
      lastCall = now;
      return func.apply(this, args);
    }
  };
}

/**
 * MotionParallax Component
 *
 * @param {string} id Unique identifier for the parallax section
 * @param {Object} settings Configuration settings
 * @param {Object} style Inline styles
 * @param {Function} children Render prop function
 * @param {string} className Custom class name
 * @param {boolean} settings.disabled Disable the parallax effect
 * @param {boolean} settings.smooth Enable smooth scrolling
 * @param {number} settings.speed Speed of the parallax effect
 * @param {string} settings.targetClassName Class name of the target element
 * @param {number} settings.maxTransformValue Maximum transform value
 * @returns {JSX.Element} rendered MotionParallax component
 */

function MotionParallax({
  id,
  settings = {},
  style = {},
  children,
  className
}) {
  const {
    disabled = false,
    smooth = true,
    speed = 1,
    targetClassName,
    maxTransformValue
  } = settings;
  const moveSectionRef = (0, _react.useRef)(null);
  const calculationFrameId = (0, _react.useRef)(null);
  const [sectionHeight, setSectionHeight] = (0, _react.useState)(0);
  const [sectionTop, setSectionTop] = (0, _react.useState)(0);
  const [sectionWidth, setSectionWidth] = (0, _react.useState)(0);
  const [targetElementHeight, setTargetElementHeight] = (0, _react.useState)(0);
  const [targetElementWidth, setTargetElementWidth] = (0, _react.useState)(0);
  const [elementOffsetTopPercent, setElementOffsetTopPercent] = (0, _react.useState)(0);
  const [elementOffsetLeftPercent, setElementOffsetLeftPercent] = (0, _react.useState)(0);
  const {
    height: windowHeight
  } = (0, _base.useWindowSize)();
  const {
    scrollY
  } = (0, _scrollContext.useScrollPosition)();
  const {
    ref: inViewRef,
    inView
  } = (0, _reactIntersectionObserver.useInView)({
    rootMargin: '50px 0px',
    triggerOnce: false
  });
  const calculateSectionBounds = (0, _react.useCallback)(() => {
    if (!moveSectionRef.current) return;
    if (calculationFrameId.current) {
      cancelAnimationFrame(calculationFrameId.current);
    }
    calculationFrameId.current = requestAnimationFrame(() => {
      const sectionElement = moveSectionRef.current;
      if (!sectionElement) return;
      const sectionRect = sectionElement.getBoundingClientRect();
      const scrollYProgress = window.scrollY;
      const sectionTopPosition = sectionRect.top + scrollYProgress;
      const sectionHeightValue = sectionRect.height;
      const sectionWidthValue = sectionRect.width;
      setSectionTop(sectionTopPosition);
      setSectionHeight(sectionHeightValue);
      setSectionWidth(sectionWidthValue);
      if (targetClassName) {
        const targetElement = sectionElement.querySelector(`.${targetClassName}`);
        if (targetElement) {
          const targetRect = targetElement.getBoundingClientRect();
          setTargetElementHeight(targetRect.height);
          setTargetElementWidth(targetRect.width);
          setElementOffsetTopPercent((targetRect.top - sectionRect.top) / sectionHeightValue * 100);
          setElementOffsetLeftPercent((targetRect.left - sectionRect.left) / sectionWidthValue * 100);
        }
      }
    });
  }, [targetClassName]);
  const effectiveInputRange = (0, _react.useMemo)(() => {
    const start = sectionTop - windowHeight;
    const end = sectionTop + sectionHeight - windowHeight;
    return [start, end];
  }, [sectionTop, sectionHeight, windowHeight]);
  const calculateScrollProgress = (0, _react.useCallback)(() => {
    if (disabled) return null;
    const [start, end] = effectiveInputRange;
    const progress = (scrollY - start) / (end - start) * speed;
    return Math.max(0, Math.min(1, progress));
  }, [disabled, effectiveInputRange, scrollY, speed]);
  const scrollProgress = calculateScrollProgress();
  (0, _react.useEffect)(() => {
    calculateSectionBounds();
    const throttledResize = throttle(calculateSectionBounds, 100);
    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (calculationFrameId.current) {
        cancelAnimationFrame(calculationFrameId.current);
      }
    };
  }, [calculateSectionBounds]);
  const setRefs = (0, _react.useCallback)(node => {
    moveSectionRef.current = node;
    inViewRef(node);
  }, [inViewRef]);
  const remainingMovementYPercent = sectionHeight - elementOffsetTopPercent - targetElementHeight;
  const remainingMovementXPercent = sectionWidth - elementOffsetLeftPercent - targetElementWidth;
  const adjustedRemainingMovementYPercent = Math.max(remainingMovementYPercent, 0);
  const adjustedRemainingMovementXPercent = Math.max(remainingMovementXPercent, 0);
  const maxTransformX = typeof maxTransformValue === 'number' ? Math.min(adjustedRemainingMovementXPercent, maxTransformValue) : adjustedRemainingMovementXPercent;
  const maxTransformY = typeof maxTransformValue === 'number' ? Math.min(adjustedRemainingMovementYPercent, maxTransformValue) : adjustedRemainingMovementYPercent;
  const clampedTransformX = scrollProgress ? scrollProgress * maxTransformX : 0;
  const clampedTransformY = scrollProgress ? scrollProgress * maxTransformY : 0;
  const smoothTransformX = (0, _scrollSpring.useSmoothTransform)(clampedTransformX, smooth, 'default');
  const smoothTransformY = (0, _scrollSpring.useSmoothTransform)(clampedTransformY, smooth, 'default');
  const finalTransformY = scrollProgress !== null ? Math.min(smoothTransformY, remainingMovementYPercent) : 0;
  const finalTransformX = scrollProgress !== null ? Math.min(smoothTransformX, remainingMovementXPercent) : 0;
  return /*#__PURE__*/_react.default.createElement(Container, {
    id: id || 'motion-parallax',
    ref: setRefs,
    className: className,
    "data-cy": "motion-parallax-container",
    style: {
      ...style
    }
  }, children({
    transformX: finalTransformX,
    transformY: finalTransformY
  }, {
    scrollY,
    sectionTop,
    sectionHeight,
    inView
  }));
}
MotionParallax.propTypes = {
  id: _propTypes.default.string,
  settings: _propTypes.default.shape({
    disabled: _propTypes.default.bool,
    smooth: _propTypes.default.bool,
    speed: _propTypes.default.number,
    targetClassName: _propTypes.default.string,
    maxTransformValue: _propTypes.default.number
  }),
  style: _propTypes.default.object,
  children: _propTypes.default.func.isRequired,
  className: _propTypes.default.string
};
var _default = exports.default = MotionParallax;